import { useMemo } from "react";
import LazyApexChart from '../../../client/components/LazyApexChart';
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor, winRateColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function PipDistributionWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tp = t.analytics.pips;

  const pipBuckets = analyticsData?.pipBuckets ?? [];

  const tableColumns = [
    { key: "bucket", label: tp.rangeHeader, align: "left" as const },
    { key: "count", label: t.analytics.numberOfTrades, align: "right" as const },
    { key: "winRate", label: t.analytics.winRateLabel, align: "right" as const },
  ];
  const tableRows = pipBuckets.map((b) => ({
    bucket: b.label,
    count: b.count,
    winRate: (
      <span style={{ color: winRateColor(b.winRate) }}>{b.winRate.toFixed(1)}%</span>
    ),
  }));
  const c = chartColors();
  const labelColor = c.axis;

  const bucketChartOptions: ApexCharts.ApexOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false }, background: "transparent", animations: { enabled: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, dataLabels: { position: "center" } } },
    colors: pipBuckets.map((b) => winRateColor(b.winRate)),
    dataLabels: {
      enabled: true,
      formatter: (v: number) => `${v}%`,
      // dark text reads on the emerald/gold/red win-rate bars in every theme
      style: { fontSize: "11px", colors: ["#0F1729"] },
    },
    xaxis: {
      categories: pipBuckets.map((b) => b.label),
      max: 100,
      labels: { style: { colors: labelColor } },
    },
    yaxis: { labels: { style: { colors: labelColor } } },
    grid: { borderColor: c.grid },
    tooltip: { theme: isDark ? "dark" : "light", y: { formatter: (v: number) => `${v}%` } },
    legend: { show: false },
  }), [isDark, pipBuckets, labelColor, c.grid]);

  if (!analyticsData || pipBuckets.length === 0) {
    return (
      <Card className="h-full">
        <CardSectionHeader label={tp.sectionTitle} isDark={isDark} />
        <CardContent className="h-full p-4">
          <EmptyState label={tp.noData} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardSectionHeader
        label={tp.sectionTitle}
        title={tp.winRateByBucket}
        isDark={isDark}
        inline
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="px-5 pb-5 pt-1">
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={tp.noData} />
        ) : (
        <>
        <LazyApexChart
          key={`pip-dist-${themeId}`}
          options={bucketChartOptions}
          series={[{ name: tp.winRateSeriesLabel, data: pipBuckets.map((b) => Math.round(b.winRate)) }]}
          type="bar"
          height={Math.max(150, pipBuckets.length * 40)}
        />
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2 text-xs text-muted-foreground">{tp.rangeHeader}</th>
                <th className="text-right p-2 text-xs text-muted-foreground">{t.journal.trades}</th>
                <th className="text-right p-2 text-xs text-muted-foreground">{t.analytics.winRate}</th>
                <th className="text-right p-2 text-xs text-muted-foreground">{t.analytics.averagePnl}</th>
              </tr>
            </thead>
            <tbody>
              {pipBuckets.map((b) => (
                <tr key={b.label} className="border-b hover:bg-muted/50">
                  <td className="p-2 font-medium">{b.label}</td>
                  <td className="p-2 text-right">{b.count}</td>
                  <td className="p-2 text-right font-mono" style={{ color: winRateColor(b.winRate) }}>
                    {b.winRate.toFixed(1)}%
                  </td>
                  <td
                    className="p-2 text-right font-mono"
                    style={{ color: pnlColor(b.avgPnl) }}
                  >
                    {b.avgPnl >= 0 ? "+" : ""}{b.avgPnl.toFixed(2)} $
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}
      </CardContent>
    </Card>
  );
}
